export type GuideActor = "customer" | "api" | "host" | "agent"

export const ACTOR_META: Record<
  GuideActor,
  { label: string; color: string; description: string }
> = {
  customer: {
    label: "Müşteri",
    color: "#0F9D58",
    description: "Mesajı yazan kişi"
  },
  api: {
    label: "api-v2",
    color: "#157CFC",
    description: "Mesajı alan ve cevabı kanala gönderen ana sunucu"
  },
  host: {
    label: "Host (kod)",
    color: "#E09100",
    description:
      "qpien-ai'nin kendi kodu: kuralları uygular, hangi araçların açık olduğuna karar verir"
  },
  agent: {
    label: "Ajan (model)",
    color: "#7B4DFF",
    description:
      "Cevabı yazan yapay zekâ: ne söyleneceğine ve hangi aracı kullanacağına karar verir"
  }
}

export type GuideSectionId =
  | "summary"
  | "journey"
  | "agent"
  | "deciders"
  | "rules"
  | "prompts"
  | "cache"
  | "scenarios"
  | "compare"
  | "glossary"

export const GUIDE_SECTIONS: { id: GuideSectionId; label: string }[] = [
  { id: "summary", label: "Tek cümlede v2" },
  { id: "journey", label: "Bir mesajın yolculuğu" },
  { id: "agent", label: "Ajanın içinde" },
  { id: "deciders", label: "Kim neye karar verir?" },
  { id: "rules", label: "Guidance, Skill, Rol" },
  { id: "prompts", label: "Promptlar" },
  { id: "cache", label: "Önbellekler" },
  { id: "scenarios", label: "Örnek senaryolar" },
  { id: "compare", label: "v1 ile farkı" },
  { id: "glossary", label: "Sözlük" }
]

export const SUMMARY_PILLARS: { title: string; body: string }[] = [
  {
    title: "Tek ajan",
    body: "Konuşmayı tek bir yapay zekâ ajanı yönetir. Geçmişi ve son mesajları okur, cevabı tek seferde yazar. Arada ayrı sınıflandırma, zenginleştirme ya da yeniden yazma adımı yoktur."
  },
  {
    title: "Çok araç (tool)",
    body: "Bilgi bankası araması, skill'ler, kanal aksiyonları ve mağaza kataloğu ayrı hatlar değil, ajanın ihtiyaç duyduğunda kullandığı araçlardır."
  },
  {
    title: "Kuralları kod tutar",
    body: "Onaysız sipariş verilmemesi, skill adımlarının atlanmaması, uydurma ürün kimliği kullanılmaması gibi kurallar prompta bırakılmaz; kod zorlar. Ajanın yazdığı metni hiçbir şey yeniden yazmaz."
  }
]

export const SUMMARY_STATS: { value: string; label: string; before: string }[] =
  [
    {
      value: "1 + araç turları",
      label: "Tur başına LLM çağrısı",
      before: "v1: 4–13"
    },
    {
      value: "2 satır",
      label: "v2'ye özel prompt",
      before: "v1: her aşama ayrı satır"
    },
    {
      value: "20,8 / 10,6 / 8,4 sn",
      label: "Dev ortamı, 3 mesaj",
      before: "v1: 32,5 / 24,8 / 17,6 sn"
    }
  ]

export type JourneyStepId =
  | "arrive"
  | "group"
  | "prepare"
  | "think"
  | "deliver"
  | "send"

export type JourneyStep = {
  id: JourneyStepId
  number: number
  title: string
  oneLine: string
  actor: GuideActor
  whatHappens: string
  example: string
  details: string[]
  v1: string
  technical: {
    auditSteps: string[]
    code: string[]
    config?: string[]
  }
}

export const JOURNEY_STEPS: JourneyStep[] = [
  {
    id: "arrive",
    number: 1,
    title: "Mesaj gelir",
    oneLine: "AI bu mesajı cevaplayacak mı? Milisaniyede karar.",
    actor: "api",
    whatHappens:
      "Müşteri bir kanaldan yazar. api-v2 mesajı konuşmaya bağlar ve qpien-ai'ye “bu mesajı cevaplayacak mısın?” diye sorar. Bu soru yapay zekâ çağırmadan, sadece ayarlara bakarak cevaplanır; bu yüzden çok hızlıdır.",
    example:
      "Instagram DM'den “Koşu ayakkabısı arıyorum” geldi. Tenant'ın E-commerce rolü bu kanalda açık ve mesai içinde: cevap “evet, cevaplayacağım”.",
    details: [
      "Uygun rol var mı diye bakılır: kanal açık mı, bu yüzeyin (chat, e-posta, ticket, yorum, marketplace sorusu…) ayarı var mı, çalışma saati uyuyor mu.",
      "Her yüzeyin kendi ayarı vardır: chat → chatSettings, ticket olmayan e-posta → emailSettings (Service ve E-commerce rolünde, chat'ten ayrı blok), e-posta ticket'ı → ticketSettings. İnsana devretmeyi kapatma ayarı yalnız chat’te vardır; e-posta her zaman insana aktarılabilir.",
      "Uygun rol yoksa AI bu mesaja hiç dokunmaz, typing göstergesi yanmaz.",
      "Evet ise bir kayıt (audit run) açılır; paneldeki AI Logs bu kaydı gösterir.",
      "Hangi motorun (v1 mi v2 mi) cevaplayacağı burada, Engine Routing ayarından okunur. api-v2 bunu bilmez.",
      "Bu kararı veren motor turun tamamını taşır: hazırlık ve cevap her zaman aynı sürümde çalışır, ayrı ayrı yönlendirilemez."
    ],
    v1: "Aynı kapı. Fark sonrasında: v1 cevabı hemen üretmeye başlar, v2 önce birkaç yüz milisaniye yeni mesaj gelecek mi diye bekler.",
    technical: {
      auditSteps: ["(run açılır)"],
      code: [
        "qpien-ai/app/src/engines/v2/capabilities/turnPrepare/turnPrepare.handler.ts",
        "POST /api/v1/turns/prepare → { willAnswer, runId, answerMode: 'async' }"
      ]
    }
  },
  {
    id: "group",
    number: 2,
    title: "Mesajlar gruplanır",
    oneLine: "Arka arkaya gelen mesajlar tek cevapla karşılanır.",
    actor: "host",
    whatHappens:
      "İnsanlar tek bir düşünceyi çoğu zaman birkaç mesajla yazar. v2 her mesaja ayrı cevap vermez: müşteri yazmayı bırakana kadar kısa bir süre bekler, sonra gelen tüm mesajları tek bir tur olarak cevaplar.",
    example:
      "“Koşu ayakkabısı arıyorum” · “42 numara” · “siyah olsun” üç saniyede geldi. v1 üç ayrı cevap verirdi; v2 son mesajdan sonra 0,7 saniye bekler ve üçüne birden tek cevap yazar.",
    details: [
      "Her konuşmanın Redis'te bir kuyruğu, bir sayacı (revision) ve bir kilidi (lease) vardır.",
      "Kilidi alan sunucu, son mesajdan sonra 0,7 sn sessizlik bekler (ilk mesajdan en fazla 2 sn).",
      "Kuyruktaki tüm mesajlar tek bir istek olarak işlenir.",
      "Cevap hazırlanırken yeni mesaj gelirse cevap gönderilmez; tüm mesajlar yeni mesajla birlikte baştan cevaplanır.",
      "Bir temsilci konuşmaya girdiyse cevap atılır."
    ],
    v1: "Her mesaj ayrı bir turdu ve ayrı cevap alıyordu. AI düşünürken temsilci konuşmaya girse bile cevap gönderiliyordu.",
    technical: {
      auditSteps: ["TURN_COORDINATION"],
      code: [
        "qpien-ai/app/src/engines/v2/services/turn/turnCoordinator.service.ts",
        "qpien-ai/app/src/engines/v2/services/turn/turnGroupProcessor.service.ts",
        "ADR 0004 — asenkron gruplanmış turlar"
      ],
      config: [
        "AI_V2_GROUP_QUIET_MS = 700",
        "AI_V2_GROUP_MAX_WAIT_MS = 2000",
        "AI_V2_LEASE_TTL_MS = 120000"
      ]
    }
  },
  {
    id: "prepare",
    number: 3,
    title: "Hazırlık",
    oneLine: "Kod, ajanın ihtiyaç duyacağı her şeyi toplar.",
    actor: "host",
    whatHappens:
      "Ajan çalışmadan önce kod, ona lazım olacak her şeyi aynı anda ve yapay zekâ kullanmadan toplar: hangi rol cevaplayacak, tenant'ın guidance'ları, skill'leri, aksiyonları, mağaza bilgisi, fotoğraf ve sesli mesajlar. Sonra bunlarla ajanın talimatını hazırlar ve hangi araçların açık olacağına karar verir.",
    example:
      "Rol: E-commerce. Guidance: 14 kural. Bilgi bankası: var, arama açık. Mağaza: bağlı, katalog araçları açık. Skill: 3 tane, katalogda listelenir. Sesli mesaj yok.",
    details: [
      "Rol seçimi: tek uygun rol varsa o; konuşma daha önce bir role oturduysa o; değilse varsayılan rol geçici olarak cevaplar ve ajan son kararı verir.",
      "Guidance: bu tenant, kanal, yüzey ve rol için aktif kuralların hepsi talimata girer.",
      "Bilgi bankası yoksa arama aracı hiç verilmez.",
      "Mağaza aracı hangi rolün cevapladığına göre verilir: E-commerce tüm katalog, Marketplace sadece sorulan ilan, Service hiçbiri.",
      "Sesli mesaj metne çevrilir, fotoğraf doğrudan modele gider.",
      "Mesajların önüne tura özel bir not eklenir: tenant'ın yerel tarihi ve saati, haftanın günü, saat dilimi, çalışma saatleri ve şu an mesai içinde olup olmadığı, mesajın geldiği kanal (platform ve yüzey).",
      "Marketplace sorusu yine tek ve kendi başına yeterli bir cevapla karşılanır, konuşma geçmişi okunmaz. Ama aynı alıcının aynı ilan, ürün ya da soru thread'inde son 30 günde sorduğu en fazla 5 soru ve verilen cevaplar nota eklenir; başka ürünlerin soruları eklenmez. Böylece “kırmızısı da var mı?” gibi devam soruları anlaşılır.",
      "Bekleyen bir sipariş onayı varsa ve müşteri onay kodunu ya da butonunu gönderdiyse sipariş bu aşamada, ajandan önce verilir."
    ],
    v1: "Bu işlerin bir kısmı ayrı yapay zekâ çağrılarıyla yapılıyordu: mesajı zenginleştirme (enrich), rol çözümü, skill tetikleme kararı.",
    technical: {
      auditSteps: ["ROLE_SELECTION", "AUDIO_TRANSCRIPTION", "AGENT_CONTEXT"],
      code: [
        "qpien-ai/app/src/engines/v2/services/turn/turnRunner.service.ts",
        "qpien-ai/app/src/engines/v2/core/roleSelection.utils.ts",
        "qpien-ai/app/src/engines/v2/core/guidance.utils.ts",
        "qpien-ai/app/src/engines/v2/core/commerceLane.utils.ts",
        "qpien-ai/app/src/engines/v2/core/turnClock.utils.ts",
        "qpien-ai/app/src/engines/v2/core/previousQuestions.utils.ts"
      ],
      config: [
        "PREVIOUS_QUESTIONS_MAX = 5",
        "PREVIOUS_QUESTIONS_MAX_AGE_DAYS = 30"
      ]
    }
  },
  {
    id: "think",
    number: 4,
    title: "Ajan düşünür",
    oneLine: "Düşün → araç kullan → sonucu oku → cevabı yaz.",
    actor: "agent",
    whatHappens:
      "Ajan talimatı ve mesajları okur. Bir bilgiye ihtiyaç duyarsa bir araç çağırır (bilgi bankasında ara, katalogda ürün bul, skill başlat…), sonucu okur, gerekirse başka bir araç çağırır. Yeterli bilgiye sahip olunca cevabı ve kararını tek bir yapılandırılmış çıktı olarak verir.",
    example:
      "Ajan katalogda “siyah koşu ayakkabısı 42” arar, iki ürün bulur, ikisinin stok bilgisine bakar ve iki ürünü öneren bir cevap yazar. Çıktı: action = answer, productIds = [2 ürün].",
    details: [
      "Her “düşün” adımı bir model çağrısıdır. Bir turda en fazla 10 model çağrısı ve 60 saniye vardır.",
      "Bir turda en fazla 3 bilgi bankası araması yapılır; aynı arama tekrar edilmez.",
      "Ajan hangi aracı kullanacağına kendisi karar verir, ama sadece kodun o tur için açtığı araçları görebilir.",
      "Çıktının içinde cevap metni, karar (cevapla, soru sor, devret, spam, cevap verme), dil, cevaplayan rol ve konuşmanın çözülüp çözülmediği vardır.",
      "Sınır aşılırsa ya da çıktı bozuksa konuşma insana devredilir."
    ],
    v1: "Önce bir sınıflandırıcı “bu RAG mı, aksiyon mu, commerce mı?” diye karar veriyordu, sonra o hattın ayrı promptu cevabı yazıyor, en sonda finalize cevabı yeniden yazıyordu.",
    technical: {
      auditSteps: ["AGENT_MODEL_CALL", "AGENT_TOOL_CALL", "AGENT_OUTPUT"],
      code: [
        "qpien-ai/app/src/engines/v2/services/agent/conversationAgent.service.ts",
        "qpien-ai/app/src/engines/v2/core/agentOutput.schema.ts"
      ],
      config: [
        "AI_V2_MAX_MODEL_TURNS = 10",
        "AI_V2_RUN_TIMEOUT_MS = 60000",
        "AI_V2_MAX_KNOWLEDGE_SEARCHES = 3",
        "AI_V2_MAX_OUTPUT_TOKENS = 1500"
      ]
    }
  },
  {
    id: "deliver",
    number: 5,
    title: "Teslim kararı",
    oneLine: "Cevap gidecek mi, nereye? Metin değiştirilmez.",
    actor: "host",
    whatHappens:
      "Kod, ajanın cevabını değiştirmeden sadece şu soruyu cevaplar: bu cevap gönderilecek mi, nereye gidecek ve yanında ne yapılacak (etiket, not, ürün kartı, devretme)? Tek cevap hakkı olan yerlerde soru sormak ya da “sizi aktarıyorum” demek o hakkı boşa harcayacağı için bu durumlarda doğrudan insana devredilir.",
    example:
      "Trendyol ürün sorusunda ajan “Hangi bedeni soruyorsunuz?” diye soru sormak istedi. Marketplace'te müşteri cevap veremez; bu yüzden soru gönderilmez, konuşma insana devredilir.",
    details: [
      "Tek cevap hakkı olan yüzeyler: yorum, yorumun otomatik DM'i, marketplace sorusu.",
      "Spam kararı ancak tenant SPAM guidance'ı yazdıysa geçerlidir; yazmadıysa tur sessiz kalır.",
      "Marketplace cevapları platform kurallarına uygunluk kontrolünden geçer; ihlal varsa cevap tutulur.",
      "Cevap hazırlanırken müşteri yeniden yazdıysa ya da bir temsilci girdiyse cevap gönderilmez.",
      "Konuşma çözüldüyse ve rol ayarı istiyorsa kapanış notu ve etiketleri üretilir."
    ],
    v1: "Finalize adımı cevabı yeniden yazıyordu; bu da cevabı üreten adımla çelişkilere yol açıyordu.",
    technical: {
      auditSteps: [
        "MARKETPLACE_COMPLIANCE_GATE",
        "SUMMARY_TAG_ANSWER_DATA",
        "DELIVERY_GATE"
      ],
      code: [
        "qpien-ai/app/src/engines/v2/core/deliveryGate.utils.ts",
        "qpien-ai/app/src/engines/v2/services/turn/turnClosing.service.ts"
      ]
    }
  },
  {
    id: "send",
    number: 6,
    title: "api-v2 gönderir",
    oneLine: "Son bir kontrol, sonra müşteriye.",
    actor: "api",
    whatHappens:
      "Aksiyonlar (mesaj gönder, etiket ekle, devret, typing'i kapat…) kuyruk üzerinden api-v2'ye gider. api-v2 göndermeden hemen önce son bir kez bakar: bu arada müşteri yeni bir şey yazdı mı, bir temsilci konuşmayı aldı mı? Öyleyse cevabı göndermez.",
    example:
      "Cevap yola çıktıktan sonra ama gönderilmeden bir an önce temsilci konuşmayı aldı. api-v2 cevabı göndermez ve kaydına “yerini yeni duruma bıraktı” yazar.",
    details: [
      "İki ayrı kontrol bilerek var: qpien-ai'nin kontrolü boşa emek harcanmasını önler, api-v2'nin kontrolü son anı kapatır.",
      "Faturalandırma değişmedi: cevaplanan grup başına bir kez.",
      "Gönderilmeyen her cevabın nedeni mesaj kartında görünür."
    ],
    v1: "Cevap senkron olarak dönüyor ve doğrudan gönderiliyordu; son an kontrolü yoktu.",
    technical: {
      auditSteps: ["AI_TURN_SUPERSEDED (api-v2)"],
      code: [
        "Rabbit: QPIEN_AI / APPLY_AI_ACTION",
        "SEND_MESSAGE.expectedLastInboundMessageId"
      ]
    }
  }
]

export type AgentTool = {
  name: string
  title: string
  what: string
  whenAvailable: string
  guardrail: string
}

export const AGENT_TOOLS: AgentTool[] = [
  {
    name: "search_knowledge",
    title: "Bilgi bankasında ara",
    what: "Tenant'ın bilgi kaynaklarında (ve istenirse hazır cevaplarında) arama yapar. Sorguyu ajan kendisi yazar.",
    whenAvailable: "Tenant'ın kullanılabilir bir bilgi kaynağı varsa.",
    guardrail:
      "Turda en fazla 3 arama, aynı sorgu tekrar edilmez. Ürüne bağlı turda ürüne özel bilgi genel bilginin yanında okunur. Belirli kanallara kısıtlanmış bir bilgi kaynağı yalnız o kanallardan gelen turlarda aranır (kanal seçilmemişse her kanalda)."
  },
  {
    name: "start_skill · skill_*",
    title: "Skill'ler",
    what: "Tenant'ın yazdığı prosedürleri (iade, randevu, kargo takibi…) başlatır ve adım adım yürütür.",
    whenAvailable:
      "Rolün ve yüzeyin yayınlanmış skill'i varsa. 12'den fazlaysa find_skill de gelir.",
    guardrail:
      "Adım atlanamaz. Adımdaki yazma işlemi yapılmadan ya da gerekçesiyle atlanmadan adım kapanmaz."
  },
  {
    name: "action_<ad>_<id>",
    title: "Kanal aksiyonları",
    what: "Tenant'ın kanala bağladığı API çağrıları (örn. sipariş durumu sorgula, kayıt oluştur).",
    whenAvailable: "Kanalda aksiyon tanımlıysa, her aksiyon için bir araç.",
    guardrail:
      "Okuma hemen çalışır. Yazma ilk seferde çalışmaz: müşteri değerleri sonraki bir mesajda onaylamalıdır. Aynı yazma iki kez çalışmaz."
  },
  {
    name: "MCP katalog araçları",
    title: "Mağaza kataloğu",
    what: "qpien-shop üzerinden ürün arama, ürün detayı, stok, varyant, sipariş durumu.",
    whenAvailable:
      "Mağaza bağlıysa ve cevaplayan rol kataloğa erişebiliyorsa (E-commerce: tüm katalog, Marketplace: sadece sorulan ilan).",
    guardrail:
      "Konuşmada daha önce görülmemiş bir ürün ya da sipariş kimliği kullanılamaz; uydurma kimlik reddedilir."
  },
  {
    name: "commerce_prepare_order · _cancellation",
    title: "Sipariş / iptal hazırlama",
    what: "Sepeti mağazanın ödeme linkine çevirir ya da kapıda ödemeli siparişi/iptali hazırlar; kapıda ödeme ve iptalde müşteriye tek kullanımlık bir onay kodu ve buton gösterilir.",
    whenAvailable:
      "Kanal sipariş alabiliyorsa ve mağaza Alışveriş Asistanı'nda ilgili anahtarı (sipariş al / kapıda ödeme / iptal) açtıysa.",
    guardrail:
      "Ödeme linki onaysız gelir, çünkü bağlayıcı değildir; sipariş müşteri mağazanın ödeme sayfasında ödeyince oluşur. Kapıda ödemeli siparişi ve iptali ajan asla kendisi veremez: müşteri kodu ya da butonu gönderdiğinde kod tarafından tam bir kez yapılır."
  }
]

export const AGENT_OUTPUT_FIELDS: { field: string; meaning: string }[] = [
  {
    field: "action",
    meaning: "Ne yapılacak: cevapla · soru sor · devret · spam · cevap verme"
  },
  { field: "reply", meaning: "Müşteriye gidecek metin (değiştirilmez)" },
  { field: "dmReply", meaning: "Sadece yorumda: yanında giden özel mesaj" },
  { field: "language", meaning: "Cevabın dili" },
  { field: "answeringRole", meaning: "Hangi rol cevaplıyor" },
  {
    field: "conversationState",
    meaning: "Konuşma sürüyor mu, çözüldü mü (kapanış notunu tetikler)"
  },
  { field: "handoffReason", meaning: "Neden insana ihtiyaç var" },
  {
    field: "knowledgeGap",
    meaning: "Bilgi bankasının cevaplayamadığı soru (bilgi boşluğu kaydı açar)"
  },
  { field: "productIds", meaning: "Cevabın önerdiği ürünler (kartları gider)" },
  { field: "notes", meaning: "Sadece kayıt için kısa açıklama" }
]

export const DECIDERS: {
  actor: GuideActor
  headline: string
  items: string[]
}[] = [
  {
    actor: "agent",
    headline: "Ne söyleneceğine",
    items: [
      "Cevabın metni ve dili",
      "Hangi aracı ne zaman kullanacağı",
      "Hangi skill'i başlatacağı",
      "Hangi guidance kuralının bu mesaja uyduğu",
      "Belirsizse hangi rolün cevaplaması gerektiği",
      "Konuşma çözüldü mü"
    ]
  },
  {
    actor: "host",
    headline: "Nelerin mümkün olduğuna",
    items: [
      "O turda hangi araçların açık olduğu",
      "Yazma işleminin ne zaman çalışacağı",
      "Skill adımının ne zaman kapanacağı",
      "Cevabın gönderilip gönderilmeyeceği",
      "Mesajların gruplanması",
      "Temsilci girerse ne olacağı"
    ]
  },
  {
    actor: "api",
    headline: "Gerçek gönderime",
    items: [
      "Mesajı kanala göndermek",
      "Göndermeden önceki son kontrol",
      "Devredilen konuşmaya temsilci seçmek",
      "Faturalandırma"
    ]
  }
]

export type RuleTopic = {
  id: "guidance" | "skills" | "roles"
  title: string
  lead: string
  steps: string[]
  pitfall: string
}

export const RULE_TOPICS: RuleTopic[] = [
  {
    id: "guidance",
    title: "Guidance",
    lead: "Guidance seçilmez, filtrelenir: filtreden geçen tüm kurallar ajanın talimatına girer, hangisinin bu mesaja uyduğunu ajan değerlendirir.",
    steps: [
      "Aktif kurallar tenant, kanal, yüzey ve rol ile filtrelenir.",
      "Beş kategoriye ayrılır: İletişim stili, Netleştirme, Devretme, Spam, Diğer.",
      "Hepsi talimatın “Guidance from the business” bölümüne yazılır.",
      "Toplam 24.000 karakteri geçerse sadece “Diğer” kategorisi kırpılır; anahtar kelimesi mesajda geçen kural her durumda kalır.",
      "Spam kararı sadece tenant SPAM kuralı yazdıysa verilebilir."
    ],
    pitfall:
      "Chat ve ticket turlarında yüzey filtresi uygulanmaz: sadece yorum için yazılmış bir kural chat cevaplarında da görünür."
  },
  {
    id: "skills",
    title: "Skill",
    lead: "Skill, tenant'ın düz yazıyla yazdığı adım adım bir prosedürdür. Ajan onu okuyup uygular, kod adımların sırasını ve güvenliğini korur.",
    steps: [
      "Kanala, mesaj tipine ve role uyan skill'ler seçilir. Mesaj tipi üç kovadır: yorum (marketplace sorusu dahil), chat ve e-posta (e-posta thread'i ve e-posta ticket'ı). Mesaj tipi seçilmemiş skill her tipte çalışır.",
      "Yayınlanmış skill'ler talimatta katalog olarak listelenir (12'den fazlaysa mesaja en yakın 12'si).",
      "Ajan mesaja bir skill'in uyduğunu düşünürse start_skill çağırır; prosedürün tamamı ona verilir.",
      "Sonraki mesajlarda ajana “şu skill'in şu adımındasın” notu eklenir.",
      "Adımdaki çipler (aksiyon, bilgi, devretme…) sadece o adımda kullanılabilir.",
      "Skill sürerken müşteri yan bir soru sorarsa ajan cevaplar, skill açık kalır."
    ],
    pitfall:
      "Yazma çipi olan bir adım, çip çalışmadan ya da “bu dal alınmadı” gerekçesi verilmeden kapanmaz. Skill ilerlemiyorsa ilk bakılacak yer burası."
  },
  {
    id: "roles",
    title: "Rol",
    lead: "Rol yapay zekâ ile değil kuralla seçilir; tek belirsiz durumda son kararı cevabı yazan ajan verir.",
    steps: [
      "Service: chat, e-posta, ticket, yorum, özel yanıt.",
      "E-commerce: aynıları, katalogla birlikte.",
      "Marketplace: sadece marketplace soruları.",
      "Tek uygun rol varsa o cevaplar; konuşma bir role oturduysa o devam eder; değilse varsayılan rol geçici cevaplar ve ajan rolü seçer.",
      "Oturmuş bir rolden sadece yetenek kazandıran yöne geçilir (bugün: E-commerce'e)."
    ],
    pitfall:
      "Rol, guidance'ın, skill'lerin ve katalog araçlarının hangi kapsamdan okunacağını da belirler. Yanlış rol = yanlış kurallar ve yanlış araçlar."
  }
]

export type GuidePrompt = {
  name: string
  layer: "v2" | "BASE"
  required: boolean
  purpose: string
  when: string
}

export const GUIDE_PROMPTS: GuidePrompt[] = [
  {
    name: "CONVERSATION_AGENT",
    layer: "v2",
    required: true,
    purpose:
      "Ajanın tüm talimatı. v1'in ayrı aşamalarının hepsi burada birleşti: kurallar, yüzeylere göre değişen metin (bayraklar), araç açıklamaları ve tura özel notlar (bloklar).",
    when: "Her turda."
  },
  {
    name: "SESSION_COMPACTION",
    layer: "v2",
    required: false,
    purpose:
      "Uzun konuşmaların eski kısmını özetler. Yoksa kod basit bir özet çıkarır; tur bozulmaz.",
    when: "Geçmiş 8.000 token'ı geçince, cevap gittikten sonra."
  },
  {
    name: "SUMMARY_TAG_ANSWER_DATA",
    layer: "BASE",
    required: false,
    purpose:
      "Kapanış notu ve etiketleri üretir. v1 ile ortaktır: burada yapılan değişiklik iki motoru da etkiler.",
    when: "Konuşma çözüldüyse ve rol ayarı istiyorsa."
  },
  {
    name: "POST_IMAGE_SUMMARY",
    layer: "BASE",
    required: false,
    purpose:
      "Yorum yapılan gönderinin görselini bir kez özetler. v1 ile ortaktır.",
    when: "Yorum ve özel yanıt turlarında, gönderi başına bir kez."
  },
  {
    name: "IMAGE_PRODUCT_MATCH",
    layer: "v2",
    required: false,
    purpose:
      "Müşterinin fotoğrafını ya da post görselini mağazanın görsel indeksinden gelen aday ürünlerle karşılaştırıp hangisi olduğuna (veya hiçbiri olmadığına) karar verir. Yalnızca E-ticaret rolü; marketplace'te kullanılmaz. Yoksa görselden ürün tanıma kapalı kalır; tur bozulmaz.",
    when: "E-ticaret rolü cevaplıyor, paket görsel tanımayı içeriyor, storefront görselleri indekslenmiş ve turda bir görsel varsa."
  }
]

export const PROMPT_LAYER_LADDER: string[] = [
  "(ad, rol, v2) — v2'nin bu role özel kopyası",
  "(ad, rolsüz, v2) — v2'nin ortak kopyası",
  "(ad, rol, BASE) — tüm motorların bu role özel satırı",
  "(ad, rolsüz, BASE) — tüm motorların ortak satırı"
]

export const PROMPT_STRUCTURES: {
  name: string
  syntax: string
  meaning: string
}[] = [
  {
    name: "Bayrak",
    syntax: "{{#if_comment}}…{{else_comment}}…{{/if_comment}}",
    meaning:
      "Metni o tura göre açar ya da kapatır: yorum mu, mağaza var mı, tek cevap hakkı mı…"
  },
  {
    name: "Değişken",
    syntax: "{{business_name}}",
    meaning:
      "Tenant verisi: işletme adı, persona, guidance, skill kataloğu, aksiyonlar."
  },
  {
    name: "Blok",
    syntax: "{{#block:tool_search_knowledge}}",
    meaning:
      "Kodun kendisinin yerleştirdiği parçalar: araç açıklamaları ve tura özel notlar."
  }
]

export const PROMPT_CACHE_ORDER: { title: string; body: string }[] = [
  {
    title: "1. Motorun sabit kuralları",
    body: "Tüm tenant'larda aynı metin. Önbellekte en uzun süre ve en geniş paylaşılan kısım."
  },
  {
    title: "2. Tenant'ın bölümü",
    body: "İşletme adı, persona, guidance, skill ve aksiyon kataloğu. Aynı tenant'ın turları arasında paylaşılır."
  },
  {
    title: "3. Tura özel notlar",
    body: "Yerel tarih ve saat, çalışma saati, kanal, mesaj sayısı, skill durumu, commerce olayları, marketplace'te aynı ilan ya da üründeki önceki sorular. Talimata hiç girmez, input'a eklenir; böylece üstteki kısım bozulmaz."
  }
]

export const PROMPT_CACHE_WINS: string[] = [
  "Aynı tur içinde: ajan bir araç çağırıp yeniden düşündüğünde ikinci çağrının başı birincisiyle aynıdır. En büyük kazanç burada.",
  "Aynı konuşmanın sonraki turlarında: talimat ve geçmiş başta aynı kalır, yeni mesajlar sona eklenir.",
  "Tenant'lar arasında: sadece motorun sabit kurallarının ortak kısmı."
]

export const PROMPT_CACHE_LIMITS: string[] = [
  "Bayraklar (yorum mu, mağaza var mı…) kuralların içinde açılır; iki istek ancak ilk farklı bayrağa kadar aynı başlangıcı paylaşır.",
  "Araç listesi ve çıktı şeması da isteğin parçasıdır; kanal aksiyonları ve katalog araçları tenant'a özel olduğu için tenant'lar arası paylaşım sınırlıdır.",
  "Session özetlendiğinde geçmiş değişir; o noktadan sonrası yeniden önbelleğe alınır.",
  "Prompt satırı düzenlenince o satırın önbelleği sıfırlanır."
]

export type DataCache = {
  name: string
  lifetime: string
  refresh: string
  saves: string
}

export const DATA_CACHES: DataCache[] = [
  {
    name: "Prompt satırları",
    lifetime: "24 saat",
    refresh: "Admin panel import'u anında siler",
    saves: "Her turda Mongo'dan prompt okunmaz"
  },
  {
    name: "Motor yönlendirmesi (v1 / v2)",
    lifetime: "60 saniye",
    refresh: "Her Engine Routing değişikliği siler",
    saves: "Her istekte yönlendirme dokümanı okunmaz; geçiş yine anında olur"
  },
  {
    name: "Guidance",
    lifetime: "24 saat",
    refresh:
      "Guidance ekleme / güncelleme / silme tenant'ın tüm kayıtlarını siler",
    saves: "Aktif kurallar her turda sorgulanmaz"
  },
  {
    name: "Mağaza araç tanımları (MCP)",
    lifetime: "5 dakika",
    refresh: "Süre dolunca; şema uyuşmazlığında sunucuya yeniden sorulur",
    saves: "Her turda MCP oturumu açıp araç listesi istenmez"
  },
  {
    name: "Sayfa → ürün eşleşmesi",
    lifetime: "10 dakika",
    refresh: "Süre dolunca",
    saves:
      "Web sohbetinde sayfanın hangi ürün olduğu tekrar çözülmez; ürün olmayan sayfa da hatırlanır"
  },
  {
    name: "Post görseli özeti",
    lifetime: "7 gün",
    refresh: "Süre dolunca",
    saves: "Bir postun altındaki yüzlerce yorum için tek görsel analizi"
  },
  {
    name: "Görsel → ürün eşleşmesi",
    lifetime: "6 saat",
    refresh: "Süre dolunca",
    saves:
      "Aynı post görseli için indeks araması ve görsel doğrulama tekrar yapılmaz; “ürün yok” sonucu da hatırlanır"
  },
  {
    name: "Tenant, kişi, ürün ayarları",
    lifetime: "api-v2 belirler",
    refresh: "api-v2 kendi yazma yolunda",
    saves: "qpien-ai bu kayıtları sadece okur, hiç yazmaz; yoksa Mongo'dan okur"
  }
]

export const CACHE_PITFALLS: string[] = [
  "Promptu Mongo'ya doğrudan yazmak önbelleği silmez: 24 saate kadar eski prompt servis edilir. Tek yol admin panel import'u.",
  "AI profilleri önbellekten okunmuyor: önbellek tanımlı ama kullanılmıyor, profiller her turda Mongo'dan gelir.",
  "Bilgi bankası aramaları önbelleğe alınmaz: aynı turda tekrar edilmez ama turlar arasında her arama baştan yapılır.",
  "Konuşma kuyruğu, kilit ve mesaj durumu da Redis'tedir ama önbellek değil geçici durumdur; kaybolursa bir sonraki mesaj akışı yeniden başlatır."
]

export type ScenarioStep = {
  actor: GuideActor
  journey: JourneyStepId
  text: string
}

export type Scenario = {
  id: string
  title: string
  lead: string
  steps: ScenarioStep[]
}

export const SCENARIOS: Scenario[] = [
  {
    id: "burst",
    title: "Art arda üç mesaj",
    lead: "Müşteri tek bir isteği üç mesaja bölüyor.",
    steps: [
      {
        actor: "customer",
        journey: "arrive",
        text: "“Koşu ayakkabısı arıyorum” yazar."
      },
      {
        actor: "api",
        journey: "arrive",
        text: "qpien-ai'ye sorar; cevap: “cevaplayacağım, sonra kuyruktan göndereceğim”. Typing göstergesi yanar."
      },
      {
        actor: "customer",
        journey: "group",
        text: "Bir saniye sonra “42 numara”, bir saniye sonra “siyah olsun” yazar."
      },
      {
        actor: "host",
        journey: "group",
        text: "Son mesajdan sonra 0,7 sn sessizlik bekler, üç mesajı tek istekte birleştirir."
      },
      {
        actor: "host",
        journey: "prepare",
        text: "Rol E-commerce, katalog araçları açık; talimatı hazırlar."
      },
      {
        actor: "agent",
        journey: "think",
        text: "Katalogda “siyah koşu ayakkabısı 42” arar, iki ürün bulur, stoklarına bakar."
      },
      {
        actor: "agent",
        journey: "think",
        text: "İki ürünü öneren tek bir cevap yazar; productIds'e iki ürünü koyar."
      },
      {
        actor: "host",
        journey: "deliver",
        text: "Yeni mesaj gelmedi, temsilci yok: cevap ve iki ürün kartı gönderilecek."
      },
      {
        actor: "api",
        journey: "send",
        text: "Son kontrolden geçer, müşteriye tek cevap ve kartlar gider."
      }
    ]
  },
  {
    id: "order",
    title: "Onaylı sipariş",
    lead: "Müşteri bir ürünü sipariş etmek istiyor.",
    steps: [
      {
        actor: "customer",
        journey: "arrive",
        text: "“Siyah olanı alayım” yazar."
      },
      {
        actor: "agent",
        journey: "think",
        text: "commerce_prepare_order çağırır. Sipariş hazırlanır ama verilmez."
      },
      {
        actor: "host",
        journey: "think",
        text: "Öneriyi kaydeder, tek kullanımlık bir kod (QPN-XXXXXXXX) ve onay butonu üretir."
      },
      {
        actor: "agent",
        journey: "think",
        text: "Sipariş özetini, kodu ve butonu içeren cevabı yazar."
      },
      {
        actor: "customer",
        journey: "arrive",
        text: "Onay butonuna basar."
      },
      {
        actor: "host",
        journey: "prepare",
        text: "Butonu tanır; siparişi ajandan önce, tam bir kez verir."
      },
      {
        actor: "agent",
        journey: "think",
        text: "“Sipariş verildi” bilgisini alır, müşterinin dilinde onay mesajı yazar."
      },
      {
        actor: "api",
        journey: "send",
        text: "Onay mesajı müşteriye gider."
      }
    ]
  },
  {
    id: "skill",
    title: "Skill ile iade",
    lead: "Tenant'ın “İade talebi” skill'i var: sipariş no al → siparişi sorgula → iade kaydı oluştur.",
    steps: [
      {
        actor: "customer",
        journey: "arrive",
        text: "“Ürünü iade etmek istiyorum” yazar."
      },
      {
        actor: "agent",
        journey: "think",
        text: "Katalogda “İade talebi” skill'ini görür, start_skill çağırır, 1. adımı okur ve sipariş numarasını sorar."
      },
      {
        actor: "customer",
        journey: "arrive",
        text: "Sipariş numarasını yazar."
      },
      {
        actor: "agent",
        journey: "think",
        text: "2. adımdaki okuma aksiyonuyla siparişi sorgular, adımı kapatır."
      },
      {
        actor: "agent",
        journey: "think",
        text: "3. adımdaki yazma aksiyonunu çağırır."
      },
      {
        actor: "host",
        journey: "think",
        text: "İlk yazma çağrısını reddeder, değerleri geri verir: “önce müşteriden onay al”."
      },
      {
        actor: "agent",
        journey: "think",
        text: "Değerleri müşteriye okur ve onay ister."
      },
      {
        actor: "customer",
        journey: "arrive",
        text: "“Evet” yazar."
      },
      {
        actor: "host",
        journey: "think",
        text: "Aynı değerler yeni bir mesajda geldi: iade kaydı bir kez oluşturulur."
      },
      {
        actor: "agent",
        journey: "think",
        text: "Skill'i tamamlar, müşteriye iade kaydının oluştuğunu yazar."
      }
    ]
  }
]

export const COMPARISON: { topic: string; v1: string; v2: string }[] = [
  {
    topic: "Yapı",
    v1: "Birbirine bağlı ayrı promptlar zinciri",
    v2: "Tek ajan + araçlar + kodun kuralları"
  },
  {
    topic: "Tur başına LLM çağrısı",
    v1: "4–13",
    v2: "1 + araç turları (en fazla 10)"
  },
  {
    topic: "Hangi hat?",
    v1: "Sınıflandırıcı karar verir",
    v2: "Ajan aracını kendisi seçer"
  },
  {
    topic: "Cevabın son hali",
    v1: "Finalize yeniden yazar",
    v2: "Ajanın metni aynen gider"
  },
  {
    topic: "Guidance",
    v1: "Anahtar kelimeyle ön elenir, finalize uygular",
    v2: "Tamamı talimatta, cevabı yazan model uygular"
  },
  {
    topic: "Skill",
    v1: "3 ayrı karar promptu",
    v2: "Aynı ajanın araçları, kurallar kodda"
  },
  {
    topic: "Art arda mesajlar",
    v1: "Her mesaja ayrı cevap",
    v2: "Gruplanır, tek cevap"
  },
  {
    topic: "Marketplace'te devam sorusu",
    v1: "Önceki sorular görülmez",
    v2: "Aynı ilan, ürün ya da thread'deki son sorular ve cevapları görülür (en fazla 5, 30 gün)"
  },
  {
    topic: "Temsilci araya girerse",
    v1: "Cevap yine gider",
    v2: "Cevap gönderilmez"
  },
  {
    topic: "Sipariş onayı",
    v1: "Onay adımı çalışmıyordu",
    v2: "Kod tarafından, tam bir kez"
  },
  {
    topic: "Geri dönüş",
    v1: "—",
    v2: "Engine Routing'den tek ayarla v1'e, deploy yok"
  }
]

export const GLOSSARY: { term: string; meaning: string }[] = [
  {
    term: "Tur (konuşma turu)",
    meaning:
      "Müşterinin art arda attığı mesajlar ve AI'ın bunlara verdiği tek cevap."
  },
  {
    term: "Model turu",
    meaning:
      "Ajanın içindeki tek bir yapay zekâ çağrısı. Bir konuşma turunda en fazla 10 olur."
  },
  {
    term: "Ajan",
    meaning: "Cevabı yazan ve hangi aracı kullanacağına karar veren yapay zekâ."
  },
  {
    term: "Host",
    meaning:
      "qpien-ai'nin kendi kodu. Kuralları uygular, araçları açar ya da kapatır, cevabın gidip gitmeyeceğine karar verir."
  },
  {
    term: "Araç (tool)",
    meaning:
      "Ajanın çağırabildiği bir yetenek: bilgi arama, skill, aksiyon, katalog."
  },
  {
    term: "Yüzey",
    meaning:
      "Mesajın geldiği yer: chat, e-posta, ticket, yorum, özel yanıt, marketplace sorusu."
  },
  {
    term: "Tek cevap hakkı",
    meaning:
      "Müşterinin aynı yerde cevap veremediği yüzeyler (yorum, marketplace sorusu). Burada soru sorulmaz."
  },
  {
    term: "Guidance",
    meaning: "Tenant'ın AI için yazdığı davranış kuralları."
  },
  {
    term: "Skill",
    meaning: "Tenant'ın düz yazıyla yazdığı adım adım prosedür."
  },
  {
    term: "Çip",
    meaning:
      "Skill adımına eklenen yetki: bir aksiyon, bilgi kaynağı, devretme, yazma…"
  },
  {
    term: "Session",
    meaning:
      "Ajanın okuduğu konuşma hafızası; araç çağrıları da içindedir. Uzayınca özetlenir."
  },
  {
    term: "BASE katmanı",
    meaning:
      "Kendi kopyası olmayan tüm motorların okuduğu prompt satırı. Değiştirmek herkesi etkiler."
  },
  {
    term: "Engine Routing",
    meaning:
      "Hangi tenant'ın hangi motorla cevaplanacağını belirleyen ayar: belirli müşteriler için pin, herkes için varsayılan, acil durum için kapatma. Turun hazırlığı ve cevabı tek ayardan yönetilir."
  },
  {
    term: "Audit run / adım",
    meaning:
      "Her turun kaydı ve içindeki adımlar. AI Logs sayfasında görünür, sorun ararken ilk bakılacak yerdir."
  }
]
